import { Row, Col, Input, message } from "antd";
import _ from "lodash";
import { useDispatch } from "react-redux";

import { ReactComponent as PlusIcon } from "@assets/images/plus-with-bg.svg";
import { useTypedSelector } from "@hooks";
import { handleOfferModeller, removeCurrency } from "@store/offers";
import {
  calculatePercentageAmount,
  getCurrencyTotal,
  getTotal,
  round,
} from "@utils";
import {
  offersApi,
  useFetchAutoPopulateDataQuery,
  useFetchComparatorDataQuery,
  useFetchCurrenciesQuery,
  useFetchOfferCurrencyRateQuery,
} from "@services";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  formatNumberDecimalEnterWithCommas,
  formatNumberDecimalWithCommas,
  formatNumberWithCommas,
} from "@/utils/formatNumberWithCommas";
import { currenciesConverter, calculatePropose } from "@utils";

const FixedCash = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const isRevised = search === "?mode=revise";
  const {
    offerModeller: {
      offer_sti: { is_sale_incentive },
    },
  } = useTypedSelector(state => state?.offers);
  const {
    offerModeller: { offer_fixed_cash, offer_sti, offer_benefits, offer_lti },
    currencyRate,
    currencyName,
    offer,
    offerCurrency,
    offer_annual,
    currentCandidateCurrency,
  } = useTypedSelector(state => state?.offers);
  const currencyCode = currencyName?.split("(")[1]?.replace(")", "") || "";
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;
  const { data } = useFetchComparatorDataQuery(
    {
      company_id,
      offer_id: offer?.id,
    },
    { skip: !offer?.id, refetchOnMountOrArgChange: true }
  );
  const { data: comparatorData } = data || {};
  const readOnly =
    offer && offer?.status !== "DRAFTED" && !isRevised ? true : false;
  const labelColSpan = currencyCode ? 10 : 12;
  const currencyColSpan = currencyCode ? 7 : 12;

  const { current_currency_total } = getCurrencyTotal(
    offer_fixed_cash,
    currentCandidateCurrency,
    [
      "current_compa_ratio",
      "current_market_ratio",
      "proposed_compa_ratio",
      "proposed_market_ratio",
    ]
  );
  const { proposed_currency_total } = getCurrencyTotal(
    offer_fixed_cash,
    currencyRate,
    [
      "current_compa_ratio",
      "current_market_ratio",
      "proposed_compa_ratio",
      "proposed_market_ratio",
    ]
  );

  const handleChange = (event: any) => {
    const cleanedValue = event.target.value.replace(/,/g, "");
    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: {
          [event.target.name]: +cleanedValue || null,
        },
      })
    );
  };
  const handleWithDecimalChange = (event: any) => {
    const cleanedValue = event.target.value.replace(/,/g, "");
    const [integerPart, decimalPart] = cleanedValue.split(".");

    // If decimal exists and more than 2 digits, ignore input
    if (decimalPart && decimalPart?.length > 2) {
      return; // stop here
    }
    if (/^\d*\.?\d*$/.test(cleanedValue)) {
      dispatch(
        handleOfferModeller({
          key: "offer_fixed_cash",
          data: {
            [event.target.name]: cleanedValue || null,
          },
        })
      );
    }
  };

  const recalculateBonuses = (base: number, allowances: any[]) => {
    const totalAllowances = allowances
      ?.filter(a => !a.is_current)
      ?.reduce((acc, a) => {
        if (a?.is_percentage && a?.percentage) {
          return acc + (base * a.percentage) / 100;
        }
        return acc + (a.value || 0);
      }, 0);

    dispatch(
      handleOfferModeller({
        key: "offer_sti",
        data: {
          bonuses: offer_sti?.bonuses?.map(bonus => {
            return bonus?.is_percentages && bonus?.is_basic_pay
              ? {
                  ...bonus,
                  value: bonus?.percentage
                    ? (base * bonus?.percentage) / 100
                    : base,
                }
              : bonus?.is_percentages && !bonus?.is_basic_pay
              ? {
                  ...bonus,
                  value:
                    bonus?.percentage && base
                      ? (round(base + totalAllowances) * bonus?.percentage) /
                        100
                      : base,
                }
              : bonus;
          }),
        },
      })
    );
  };

  useEffect(() => {
    if (
      (offer_sti?.bonuses?.length > 0 &&
        offer_fixed_cash?.proposed_annual_base) ||
      (offer_sti?.bonuses?.length > 0 && offer_fixed_cash?.proposed_total)
    ) {
      dispatch(
        handleOfferModeller({
          key: "offer_sti",
          data: {
            bonuses: [...offer_sti?.bonuses], // new reference → reducer recalculates
          },
        })
      );
      // original_value comment
      // dispatch(
      //   handleOfferModeller({
      //     key: "offer_benefits",
      //     data: {
      //       bonuses: [...offer_benefits?.bonuses], // new reference → reducer recalculates
      //     },
      //   })
      // );
    }
  }, [
    offer_sti?.bonuses?.length,
    offer_fixed_cash?.proposed_annual_base,
    offer_fixed_cash?.proposed_total,
  ]);

  useEffect(() => {
    if (offer_lti?.current_total_lti || offer_lti?.proposed_total_lti) {
      const totalDirectLTICurrent = (currentTotal: number) => {
        const result =
          Number(offer_fixed_cash?.current_total) +
          Number(offer_sti?.current_total_bonus);
        return result + Number(currentTotal);
      };
      const totalDirectLTIProposed = (proposedTotal: number) => {
        const result =
          offer_sti?.is_sale_incentive &&
          offer_fixed_cash?.proposed_annual_base &&
          offer_sti.proposed_base_pay_percentage
            ? round(
                (offer_fixed_cash.proposed_annual_base *
                  ((offer_fixed_cash.proposed_annual_base *
                    (100 - offer_sti.proposed_base_pay_percentage)) /
                    100)) /
                  ((offer_fixed_cash.proposed_annual_base *
                    offer_sti.proposed_base_pay_percentage) /
                    100) -
                  offer_sti.proposed_base_pay_percentage
              ) +
              ((offer_sti?.proposed_total_bonus || 0) +
                (offer_fixed_cash?.proposed_total || 0))
            : (offer_sti?.proposed_total_bonus || 0) +
              (offer_fixed_cash?.proposed_total || 0);
        return result + proposedTotal;
      };
      const current_total_direct_comp = totalDirectLTICurrent(
        Number(offer_lti?.current_total_lti)
      );
      const totalLtis = offer_lti?.proposed_total_lti;
      const proposed_total_direct_comp = totalDirectLTIProposed(totalLtis || 0);
      dispatch(
        handleOfferModeller({
          key: "offer_lti",
          data: {
            current_total_direct_comp: current_total_direct_comp,
            proposed_total_direct_comp: proposed_total_direct_comp,
          },
        })
      );
    }
  }, [
    offer_lti?.current_total_lti,
    offer_lti?.proposed_total_lti,
    offer_fixed_cash,
  ]);

  useEffect(() => {
    if (
      offer_benefits.current_total_bonus ||
      offer_benefits.proposed_total_bonus ||
      offer_benefits.current_benefit ||
      offer_benefits.proposed_benefit ||
      offer_lti?.current_total_lti ||
      offer_lti?.proposed_total_lti
    ) {
      const proposedLtiStockAware = (() => {
        const base = Number(offer_lti?.proposed_total_lti) || 0;
        const stock = Number((offer_lti as any)?.selected_stock_value) || 1;

        // If other_ltis exist, replace any "unit === false" entries with amount = value * stock
        const adjusted = (offer_lti?.other_ltis || [])
          .filter((x: any) => !x?.is_current)
          .reduce((sum: number, it: any) => {
            const value = Number(it?.value) || 0;
            if (!it?.unit && value) {
              // proposed_total_lti likely included raw value; replace raw with stock*value
              return sum - value + round(value * (stock || 1));
            }
            return sum;
          }, base);

        return round(adjusted || 0);
      })();
      const proposedBenefitsTotal = (offer_benefits?.bonuses || [])
        .filter(b => !b?.is_current)
        .reduce((sum, b) => {
          const value = Number(b?.value) || 0;
          const basis = b?.calculation_basis;
          // percent_basic → calculate percentage
          if (basis === "percent_basic") {
            return (
              sum +
              calculatePercentageAmount(
                Number(offer_fixed_cash?.proposed_annual_base || 0),
                value
              )
            );
          } else if (basis === "percent_guaranteed") {
            return (
              sum +
              calculatePercentageAmount(
                Number(offer_fixed_cash?.proposed_total || 0),
                value
              )
            );
          }

          // default → flat value
          return sum + value;
        }, 0);
      let totalBenefitProposed = (proposedBenefits: number) => {
        const result =
          offer_sti?.is_sale_incentive &&
          offer_fixed_cash?.proposed_annual_base &&
          offer_sti?.proposed_base_pay_percentage
            ? round(
                (offer_fixed_cash.proposed_annual_base *
                  ((offer_fixed_cash.proposed_annual_base *
                    (100 - offer_sti.proposed_base_pay_percentage)) /
                    100)) /
                  ((offer_fixed_cash.proposed_annual_base *
                    offer_sti.proposed_base_pay_percentage) /
                    100) -
                  offer_sti.proposed_base_pay_percentage
              ) +
              ((offer_sti?.proposed_total_bonus || 0) +
                (offer_fixed_cash?.proposed_total || 0))
            : (offer_sti?.proposed_total_bonus || 0) +
              (offer_fixed_cash?.proposed_total || 0);

        // ⬇️ Use stock-aware LTI here
        return result + proposedBenefits + Number(proposedLtiStockAware) || 0;
      };
      const totalBenefitCurrent = (currentTotal: number) => {
        const result =
          Number(offer_fixed_cash?.current_total) +
          Number(offer_sti?.current_total_bonus);
        return (
          result + currentTotal + Number(offer_lti?.current_total_lti) || 0
        );
      };
      const current_total_remuneration = totalBenefitCurrent(
        Number(offer_benefits?.current_total_bonus)
      );

      const proposed_total_remuneration = totalBenefitProposed(
        proposedBenefitsTotal
      ) as number;
      dispatch(
        handleOfferModeller({
          key: "offer_benefits",
          data: {
            current_total_remuneration: current_total_remuneration,
            proposed_total_remuneration: proposed_total_remuneration,
          },
        })
      );
    }
  }, [
    offer_benefits.current_total_bonus,
    offer_benefits.proposed_total_bonus,
    offer_lti?.current_total_lti,
    offer_lti?.proposed_total_lti,
    offer_fixed_cash?.proposed_annual_base,
    offer_fixed_cash?.proposed_total,
    offer_sti?.current_total_bonus,
  ]);

  const addMoreLocally = (is_current: boolean) => {
    const newCurrent = {
      is_current,
      value: null,
      name: null,
      id: +_.uniqueId(),
      readonly: true,
      is_percentage: false,
    };
    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: {
          allowances: [...offer_fixed_cash.allowances, newCurrent],
        },
      })
    );
  };

  const handleChangeNewField = (
    id: number,
    value: number | string,
    type: "name" | "value"
  ) => {
    const updatedAllowances = offer_fixed_cash?.allowances?.map(allowance =>
      allowance?.id === id ? { ...allowance, [type]: value } : allowance
    );

    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: {
          allowances: updatedAllowances as any,
        },
      })
    );

    if (type === "value") {
      recalculateBonuses(
        offer_fixed_cash?.proposed_annual_base || 0,
        updatedAllowances
      );
    }
  };

  const removeField = (id: number) => {
    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: {
          allowances: offer_fixed_cash?.allowances?.map(allowance =>
            allowance?.id === id ? { ...allowance, readonly: false } : allowance
          ) as any,
        },
      })
    );
  };

  const removeAllowance = (id: number) => {
    const updatedAllowances = offer_fixed_cash?.allowances?.filter(
      allowance => allowance?.id !== id
    ) as any;

    dispatch(
      handleOfferModeller({
        key: "offer_fixed_cash",
        data: {
          allowances: updatedAllowances,
        },
      })
    );

    recalculateBonuses(
      offer_fixed_cash?.proposed_annual_base || 0,
      updatedAllowances
    );
  };

  let id = comparatorData?.currency?.market_currency?.id;
  const currencyId = typeof id === "string" ? Number(id) : id;

  const {
    data: counrtyList,
    error,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    refetch,
  } = useFetchOfferCurrencyRateQuery(
    {
      // code: 138,
      // code: comparatorData?.currency?.offer_currency?.id,
      code: offer?.offer_candidate_detail?.currency?.id,
      company_id,
      offer_id: offer?.id,
    },
    {
      refetchOnMountOrArgChange: true,
      // skip: !comparatorData?.currency?.market_currency?.id,
    }
  );
  useFetchAutoPopulateDataQuery(
    {
      company_id,
      offer_id: offer?.id,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !offerCurrency,
    }
  );

  useEffect(() => {
    // if (id && id != offer?.offer_position_detail?.currency?.id) {
    //   dispatch(
    //     offersApi.endpoints.fetchOfferCurrencyRate.initiate(
    //       {
    //         code: id,
    //         company_id,
    //         offer_id: offer?.id,
    //       },
    //       {
    //         subscribe: false,
    //         forceRefetch: true,
    //       }
    //     )
    //   );

    //   // updateNew();
    // }

    dispatch(removeCurrency());
  }, [id]);

  // let updateNew = () => {

  //   if(offer_fixed_cash?.allowances.length >0){
  //     let val = offer_fixed_cash?.proposed_annual_base;

  //     dispatch(
  //       handleOfferModeller({
  //         key: "offer_fixed_cash",
  //         data: {
  //           proposed_annual_base: val || null,
  //           proposed_compa_ratio:
  //             (comparatorData?.internal_comparator_data?.internal_salary_range
  //               ?.mid &&
  //               val &&
  //               (offerCurrency != null ? val * offerCurrency : val) /
  //                 comparatorData?.internal_comparator_data?.internal_salary_range
  //                   ?.mid) ||
  //             null,
  //           proposed_market_ratio:
  //             (comparatorData?.external_comparator_data?.market_data_grade?.p50 &&
  //               val &&
  //               val /
  //                 comparatorData?.external_comparator_data?.market_data_grade
  //                   ?.p50) ||
  //             null,
  //         },
  //       })
  //     );

  //   }

  // };

  // useEffect(() => {
  //   updateNew();
  // }, [offerCurrency]);

  const { data: currenciesData, isLoading: isFetchingCurrencies } =
    useFetchCurrenciesQuery(null);
  const { data: currencies } = currenciesData || {};

  // const valueFecth = useFetchOfferCurrencyRateQuery(
  //   {
  //     code: currencyId,
  //     company_id,
  //     offer_id: offer?.id,
  //   },
  //   {
  //     refetchOnMountOrArgChange: true,
  //     skip: !comparatorData?.currency?.market_currency?.id,
  //   }
  // );
  // )
  const currRa = currenciesConverter(
    counrtyList?.data?.result || 1,
    offer_fixed_cash?.proposed_total
  );
  const difference = calculatePropose(offer_fixed_cash?.current_total, currRa);
  const proposed_compa_ratio =
    Number(offer_fixed_cash?.proposed_annual_base) /
    Number(
      comparatorData?.internal_comparator_data?.internal_salary_range?.mid
    );

  return (
    <>
      <Row className="salary-offer">
        <Col className="salary-offer__parent-col" span={12}>
          <Row className="salary-offer__header-row cursor-pointer border-b border-themeBorder bg-themeCore">
            <Col span={12}>
              <div className="sub-heading font-semibold text-lg">Current</div>
            </Col>
          </Row>

          <Row className="salary-offer__content-row">
            <Col className="salary-offer__content-row__col" span={labelColSpan}>
              <div className="salary-offer__content-row__field-upper"></div>

              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field"
                    : "salary-offer__content-row__field_extra"
                } `}
              >
                <label>Annual Base</label>
              </div>
              <div className="salary-offer__content-row__field">
                <label>Compa Ratio</label>
              </div>
              <div className="salary-offer__content-row__field">
                <label>Market Ratio</label>
              </div>

              {offer_fixed_cash?.allowances
                ?.filter(({ is_current }) => is_current)
                ?.map(allowance => {
                  const { name, id } = allowance;
                  return (
                    <div
                      key={id}
                      className={` ${
                        offer_annual
                          ? "salary-offer__content-row__field"
                          : "salary-offer__content-row__field_extra"
                      } `}
                    >
                      {name?.length && !allowance?.readonly! ? (
                        <label>{name}</label>
                      ) : (
                        <Input
                          className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                          readOnly={readOnly}
                          autoFocus
                          onChange={e =>
                            handleChangeNewField(id!, e?.target?.value, "name")
                          }
                          onKeyDown={e => {
                            if (e.key === "Enter") {
                              removeField(id!);
                            }
                          }}
                          value={name || ""}
                          placeholder="Value here..."
                          size="large"
                        />
                      )}
                      {!readOnly && (
                        <div
                          className="salary-offer__content-row__field__remove"
                          onClick={() => removeAllowance(id!)}
                        >
                          <PlusIcon fill="#000" />
                        </div>
                      )}
                    </div>
                  );
                })}

              {!readOnly ? (
                <div
                  onClick={() => addMoreLocally(true)}
                  className="salary-offer__content-row__field--add-more"
                >
                  <PlusIcon fill="#4FAA89" />
                  <span>Add more</span>
                </div>
              ) : (
                <div className="salary-offer__content-row__field--add-more" />
              )}
            </Col>

            <Col
              className="salary-offer__content-row__col"
              span={currencyColSpan}
            >
              <div className="salary-offer__content-row__field-upper">
                {offer?.offer_candidate_detail?.currency?.code}
              </div>
              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field"
                    : "salary-offer__content-row__field_extra"
                } `}
              >
                <div className="w-full">
                  <Input
                    className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                    readOnly={readOnly}
                    type="text"
                    name="current_annual_base"
                    onChange={handleChange}
                    value={formatNumberWithCommas(
                      offer_fixed_cash?.current_annual_base || ""
                    )}
                    placeholder="Value here..."
                    size="large"
                  />

                  {!offer_annual && (
                    <p className="pl-1">
                      Monthly:{" "}
                      {formatNumberWithCommas(
                        round(
                          offer_fixed_cash?.current_annual_base
                            ? offer_fixed_cash?.current_annual_base / 12
                            : 0
                        )
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="salary-offer__content-row__field">
                <Input
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  readOnly={readOnly}
                  type="text"
                  name="current_compa_ratio"
                  value={offer_fixed_cash?.current_compa_ratio}
                  onChange={handleWithDecimalChange}
                  placeholder="Value here..."
                  size="large"
                />
              </div>
              <div className="salary-offer__content-row__field">
                <Input
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  readOnly={readOnly}
                  type="text"
                  name="current_market_ratio"
                  // value={formatNumberWithCommas(
                  //   offer_fixed_cash?.current_market_ratio?.toFixed(2) || ""
                  // )}
                  value={offer_fixed_cash?.current_market_ratio}
                  onChange={handleWithDecimalChange}
                  placeholder="Value here..."
                  size="large"
                />
              </div>

              {offer_fixed_cash?.allowances
                ?.filter(({ is_current }) => is_current)
                ?.map(({ name, value, id }) => (
                  <div
                    key={id}
                    className={` ${
                      offer_annual
                        ? "salary-offer__content-row__field"
                        : "salary-offer__content-row__field_extra"
                    } `}
                  >
                    <div className="w-full">
                      <Input
                        className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                        readOnly={readOnly}
                        name={name!}
                        type="text"
                        value={formatNumberWithCommas(value)}
                        onChange={e =>
                          handleChangeNewField(
                            id!,
                            +e.target.value.replace(/,/g, ""),
                            "value"
                          )
                        }
                        placeholder="Value here..."
                        size="large"
                      />

                      {!offer_annual && (
                        <p className="pl-1">
                          Monthly:{" "}
                          {formatNumberWithCommas(
                            round(value ? value / 12 : 0)
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                ))}

              <div className="salary-offer__content-row__field--add-more" />
            </Col>
            {currencyCode ? (
              <Col className="salary-offer__content-row__col" span={7}>
                <div className="salary-offer__content-row__field-upper">
                  {currencyCode}
                </div>
                <div
                  className={` ${
                    offer_annual
                      ? "salary-offer__content-row__field"
                      : "salary-offer__content-row__field_extra"
                  } `}
                >
                  <div>
                    <Input
                      type="text"
                      className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                      value={formatNumberWithCommas(
                        round(
                          offer_fixed_cash?.current_annual_base! *
                            currentCandidateCurrency!
                        ) || ""
                      )}
                      readOnly
                      placeholder="Value here..."
                      size="large"
                    />

                    {!offer_annual && (
                      <p className="pl-1">
                        Monthly:{" "}
                        {formatNumberWithCommas(
                          round(
                            offer_fixed_cash?.current_annual_base! *
                              currencyRate!
                              ? (offer_fixed_cash?.current_annual_base! *
                                  currencyRate!) /
                                  12
                              : 0
                          )
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="salary-offer__content-row__field" />
                <div className="salary-offer__content-row__field" />
                {offer_fixed_cash?.allowances
                  ?.filter(({ is_current }) => is_current)
                  ?.map(({ id, value }) => (
                    <div
                      key={id}
                      className={` ${
                        offer_annual
                          ? "salary-offer__content-row__field"
                          : "salary-offer__content-row__field_extra"
                      } `}
                    >
                      <div>
                        <Input
                          type="text"
                          className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                          value={formatNumberWithCommas(
                            round(value! * currentCandidateCurrency!) || ""
                          )}
                          readOnly
                          placeholder="Value here..."
                          size="large"
                        />

                        {!offer_annual && (
                          <p className="pl-1">
                            Monthly:{" "}
                            {formatNumberWithCommas(
                              round(
                                round(value! * currentCandidateCurrency!)
                                  ? round(value! * currentCandidateCurrency!) /
                                      12
                                  : 0
                              )
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </Col>
            ) : null}
          </Row>
        </Col>

        <Col className="salary-offer__parent-col" span={12}>
          <Row className="salary-offer__header-row cursor-pointer border-b border-themeBorder bg-themeCore">
            <Col span={12}>
              <div className="sub-heading font-semibold text-lg">Proposed</div>
            </Col>
          </Row>
          <Row className="salary-offer__content-row">
            <Col className="salary-offer__content-row__col" span={labelColSpan}>
              <div className="salary-offer__content-row__field-upper"></div>

              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field"
                    : "salary-offer__content-row__field_extra"
                } `}
              >
                <label>Annual Base</label>
              </div>
              <div className="salary-offer__content-row__field">
                <label>Compa Ratio</label>
              </div>
              <div className="salary-offer__content-row__field">
                <label>Market Ratio </label>
              </div>

              {offer_fixed_cash?.allowances
                ?.filter(({ is_current }) => !is_current)
                ?.map(({ name, id, readonly }) => (
                  <div
                    key={id}
                    className={` ${
                      offer_annual
                        ? "salary-offer__content-row__field"
                        : "salary-offer__content-row__field_extra"
                    } `}
                  >
                    {name?.length && !readonly! ? (
                      <label>{name}</label>
                    ) : (
                      <Input
                        className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                        readOnly={readOnly}
                        autoFocus
                        onChange={e =>
                          handleChangeNewField(id!, e?.target?.value, "name")
                        }
                        onKeyDown={e => {
                          if (e.key === "Enter") {
                            removeField(id!);
                          }
                        }}
                        value={name || ""}
                        placeholder="Value here..."
                        size="large"
                      />
                    )}
                    {!readOnly && (
                      <div
                        className="salary-offer__content-row__field__remove"
                        onClick={() => removeAllowance(id!)}
                      >
                        <PlusIcon fill="#000" />
                      </div>
                    )}
                  </div>
                ))}
              {!readOnly ? (
                <div
                  onClick={() => addMoreLocally(false)}
                  className="salary-offer__content-row__field--add-more"
                >
                  <PlusIcon fill="#4FAA89" />
                  <span>Add more</span>
                </div>
              ) : (
                <div className="salary-offer__content-row__field--add-more" />
              )}
            </Col>
            <Col
              className="salary-offer__content-row__col"
              span={currencyColSpan}
            >
              <div className="salary-offer__content-row__field-upper">
                {offer?.offer_position_detail?.currency?.code}
              </div>

              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field"
                    : "salary-offer__content-row__field_extra"
                } `}
              >
                <div className="w-full">
                  <Input
                    className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                    disabled={is_sale_incentive}
                    readOnly={readOnly}
                    type="text"
                    name="proposed_annual_base"
                    onChange={event => {
                      const val = +event.target.value.replace(/,/g, "");

                      const externalData =
                        comparatorData?.external_comparator_data;
                      const marketP50 = externalData?.market_data_grade?.p50;
                      const marketFunctionP50 =
                        externalData?.market_data_grade_function?.p50;

                      const numerator =
                        offerCurrency != null ? val * offerCurrency : val;
                      const denominator = marketFunctionP50 || marketP50;

                      dispatch(
                        handleOfferModeller({
                          key: "offer_fixed_cash",
                          data: {
                            proposed_annual_base: val || null,
                            proposed_compa_ratio:
                              (comparatorData?.internal_comparator_data
                                ?.internal_salary_range?.mid &&
                                val &&
                                (offerCurrency != null
                                  ? val * offerCurrency
                                  : val) /
                                  comparatorData?.internal_comparator_data
                                    ?.internal_salary_range?.mid) ||
                              null,

                            proposed_market_ratio: val
                              ? denominator > 0
                                ? numerator / denominator
                                : 0
                              : null,
                          },
                        })
                      );

                      dispatch(
                        handleOfferModeller({
                          key: "offer_fixed_cash",
                          data: {
                            allowances: offer_fixed_cash?.allowances?.map(
                              allowance => {
                                return allowance?.is_percentage
                                  ? {
                                      ...allowance,
                                      value: allowance?.percentage
                                        ? (val * allowance?.percentage) / 100
                                        : val,
                                    }
                                  : allowance;
                              }
                            ),
                          },
                        })
                      );

                      const totalAllowances =
                        offer_fixed_cash.allowances
                          ?.filter(a => !a.is_current)
                          ?.reduce((acc, a) => {
                            if (a?.is_percentage && a?.percentage) {
                              return acc + (val * a.percentage) / 100;
                            }
                            return acc + (a.value || 0);
                          }, 0) || 0;

                      dispatch(
                        handleOfferModeller({
                          key: "offer_sti",
                          data: {
                            bonuses: offer_sti?.bonuses?.map(bonus => {
                              return bonus?.is_percentages &&
                                bonus?.is_basic_pay
                                ? {
                                    ...bonus,
                                    value: bonus?.percentage
                                      ? (val * bonus?.percentage) / 100
                                      : val,
                                  }
                                : bonus?.is_percentages && !bonus?.is_basic_pay
                                ? {
                                    ...bonus,
                                    value:
                                      bonus?.percentage && val
                                        ? (round(val + totalAllowances) *
                                            bonus?.percentage) /
                                          100
                                        : val,
                                  }
                                : bonus;
                            }),
                          },
                        })
                      );
                    }}
                    value={formatNumberWithCommas(
                      is_sale_incentive
                        ? offer_fixed_cash?.proposed_annual_base || ""
                        : offer_fixed_cash?.proposed_annual_base || ""
                    )}
                    placeholder="Value here..."
                    size="large"
                  />

                  {!offer_annual && (
                    <p className="pl-1">
                      Monthly:{" "}
                      {formatNumberWithCommas(
                        round(
                          offer_fixed_cash?.proposed_annual_base
                            ? offer_fixed_cash?.proposed_annual_base / 12
                            : 0
                        )
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="salary-offer__content-row__field">
                <Input
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  readOnly
                  type="text"
                  name="proposed_compa_ratio"
                  value={formatNumberDecimalWithCommas(
                    comparatorData?.internal_comparator_data
                      ?.internal_salary_range?.mid
                      ? proposed_compa_ratio?.toFixed(2)
                      : offer_fixed_cash?.proposed_compa_ratio?.toFixed(2) || ""
                  )}
                  placeholder="Value here..."
                  size="large"
                />
              </div>
              <div className="salary-offer__content-row__field">
                <Input
                  className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                  readOnly
                  type="text"
                  name="proposed_market_ratio"
                  value={formatNumberDecimalWithCommas(
                    offer_fixed_cash?.proposed_market_ratio?.toFixed(2) || ""
                  )}
                  placeholder="Value here..."
                  size="large"
                />
              </div>

              {offer_fixed_cash?.allowances
                ?.filter(({ is_current }) => !is_current)
                ?.map(
                  ({
                    name,
                    value,
                    id,
                    is_percentage,
                    original_value,
                    is_basic_pay,
                  }: any) => (
                    <div
                      key={id}
                      className={` ${
                        offer_annual
                          ? "salary-offer__content-row__field"
                          : "salary-offer__content-row__field_extra"
                      } `}
                    >
                      <div className="w-full">
                        <Input
                          className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                          readOnly={readOnly || is_percentage}
                          name={name!}
                          contentEditable={false}
                          type="text"
                          // value={formatNumberWithCommas(
                          //   Math.round(
                          //     is_percentage &&
                          //       offer_fixed_cash.proposed_annual_base
                          //       ? Math.round(value)
                          //       : !is_percentage
                          //         ? value || ""
                          //         : ""
                          //   )
                          // )}
                          value={
                            is_percentage
                              ? formatNumberWithCommas(
                                  is_basic_pay
                                    ? calculatePercentageAmount(
                                        Number(
                                          offer_fixed_cash?.proposed_annual_base
                                        ),
                                        original_value
                                      )
                                    : calculatePercentageAmount(
                                        Number(
                                          offer_fixed_cash?.proposed_annual_base
                                        ),
                                        original_value
                                      )
                                )
                              : formatNumberWithCommas(round(value) || "")
                          }
                          onChange={e => {
                            if (!is_percentage) {
                              handleChangeNewField(
                                id!,
                                +e.target.value.replace(/,/g, ""),
                                "value"
                              );
                            }
                          }}
                          size="large"
                        />

                        {!offer_annual && (
                          <p className="pl-1">
                            Monthly:{" "}
                            {formatNumberWithCommas(
                              round(
                                is_percentage &&
                                  offer_fixed_cash.proposed_annual_base
                                  ? value / 12
                                  : !is_percentage
                                  ? (value || 0) / 12
                                  : 0
                              )
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                )}

              <div className="salary-offer__content-row__field--add-more" />
            </Col>
            {currencyCode ? (
              <Col className="salary-offer__content-row__col" span={7}>
                <div className="salary-offer__content-row__field-upper">
                  {currencyCode}
                </div>
                <div
                  className={` ${
                    offer_annual
                      ? "salary-offer__content-row__field"
                      : "salary-offer__content-row__field_extra"
                  } `}
                >
                  <div className="w-full">
                    <Input
                      type="text"
                      className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                      // value={100}
                      value={formatNumberWithCommas(
                        round(
                          offer_fixed_cash?.proposed_annual_base! *
                            currencyRate!
                        ) || ""
                      )}
                      readOnly
                      placeholder="Value here..."
                      size="large"
                    />

                    {!offer_annual && (
                      <p className="pl-1">
                        Monthly:{" "}
                        {formatNumberWithCommas(
                          round(
                            offer_fixed_cash?.proposed_annual_base! *
                              currencyRate!
                              ? (offer_fixed_cash?.proposed_annual_base! *
                                  currencyRate!) /
                                  12
                              : 0
                          )
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="salary-offer__content-row__field" />
                <div className="salary-offer__content-row__field" />
                {offer_fixed_cash?.allowances
                  ?.filter(({ is_current }) => !is_current)
                  ?.map(({ id, value }) => (
                    <div
                      key={id}
                      className={` ${
                        offer_annual
                          ? "salary-offer__content-row__field"
                          : "salary-offer__content-row__field_extra"
                      } `}
                    >
                      <div className="w-full">
                        <Input
                          type="text"
                          className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                          value={formatNumberWithCommas(
                            round(value! * currencyRate!) || ""
                          )}
                          readOnly
                          placeholder="Value here..."
                          size="large"
                        />

                        {!offer_annual && (
                          <p className="pl-1">
                            Monthly:{" "}
                            {formatNumberWithCommas(
                              round(
                                round(value! * currencyRate!)
                                  ? round(value! * currencyRate!) / 12
                                  : 0
                              )
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                <div className="salary-offer__content-row__field--add-more" />
              </Col>
            ) : null}
          </Row>
        </Col>
      </Row>

      <Row className="salary-offer">
        <Col className="salary-offer__parent-col" span={12}>
          <Row className="salary-offer__content-row__col">
            <Col className="salary-offer__content-row__col" span={labelColSpan}>
              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field  salary-offer__content-row__field--total"
                    : "salary-offer__content-row__field_extra  salary-offer__content-row__field--total"
                } `}
              >
                <label>Total Guaranteed Cash</label>
              </div>
            </Col>

            <Col
              className="salary-offer__content-row__col"
              span={currencyColSpan}
            >
              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field  salary-offer__content-row__field--total"
                    : "salary-offer__content-row__field_extra  salary-offer__content-row__field--total"
                } `}
              >
                <div className="w-full">
                  <Input
                    className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                    readOnly
                    name="current_total"
                    value={formatNumberWithCommas(
                      offer_fixed_cash?.current_total || ""
                    )}
                    placeholder="Value here..."
                    size="large"
                  />
                  {!offer_annual && (
                    <p className="pl-1">
                      Monthly:{" "}
                      {formatNumberWithCommas(
                        round(
                          offer_fixed_cash?.current_total
                            ? offer_fixed_cash?.current_total / 12
                            : 0
                        )
                      )}
                    </p>
                  )}
                </div>
              </div>
            </Col>

            {currencyCode ? (
              <Col className="salary-offer__content-row__col" span={7}>
                <div
                  className={` ${
                    offer_annual
                      ? "salary-offer__content-row__field  salary-offer__content-row__field--total"
                      : "salary-offer__content-row__field_extra  salary-offer__content-row__field--total"
                  } `}
                >
                  <div>
                    <Input
                      className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                      value={formatNumberWithCommas(
                        current_currency_total || ""
                      )}
                      readOnly
                      placeholder="Value here..."
                      size="large"
                    />
                    {!offer_annual && (
                      <p className="pl-1">
                        Monthly:{" "}
                        {formatNumberWithCommas(
                          round(
                            current_currency_total
                              ? current_currency_total / 12
                              : 0
                          )
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </Col>
            ) : null}
          </Row>
        </Col>

        <Col className="salary-offer__parent-col" span={12}>
          <Row className="salary-offer__content-row__col">
            <Col className="salary-offer__content-row__col" span={labelColSpan}>
              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field  salary-offer__content-row__field--total"
                    : "salary-offer__content-row__field_extra  salary-offer__content-row__field--total"
                } `}
              >
                <label>Total Guaranteed Cash</label>
              </div>
            </Col>

            <Col
              className="salary-offer__content-row__col"
              span={currencyColSpan}
            >
              <div
                className={` ${
                  offer_annual
                    ? "salary-offer__content-row__field  salary-offer__content-row__field--total"
                    : "salary-offer__content-row__field_extra  salary-offer__content-row__field--total"
                } `}
              >
                <div className="w-full">
                  <Input
                    className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                    readOnly
                    name="proposed_total"
                    value={formatNumberWithCommas(
                      offer_fixed_cash?.proposed_total?.toFixed() || ""
                    )}
                    placeholder="Value here..."
                    size="large"
                    type="text"
                  />

                  {!offer_annual && (
                    <p className="pl-1">
                      Monthly:{" "}
                      {formatNumberWithCommas(
                        round(
                          offer_fixed_cash?.proposed_total
                            ? offer_fixed_cash?.proposed_total / 12
                            : 0
                        )
                      )}
                    </p>
                  )}
                </div>
              </div>
            </Col>

            {currencyCode ? (
              <Col className="salary-offer__content-row__col" span={7}>
                <div
                  className={` ${
                    offer_annual
                      ? "salary-offer__content-row__field  salary-offer__content-row__field--total"
                      : "salary-offer__content-row__field_extra  salary-offer__content-row__field--total"
                  } `}
                >
                  <div className="w-full">
                    <Input
                      className={`w-full shadow-md my-2 font-semibold text-sm outline-none border border-themeInputBorder bg-white px-3 py-3 rounded-md`}
                      value={proposed_currency_total || ""}
                      readOnly
                      placeholder="Value here..."
                      size="large"
                    />

                    {!offer_annual && (
                      <p className="pl-1">
                        Monthly:{" "}
                        {formatNumberWithCommas(
                          round(
                            proposed_currency_total
                              ? proposed_currency_total / 12
                              : 0
                          )
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </Col>
            ) : null}
          </Row>
        </Col>
      </Row>

      <Row className="salary-offer">
        <Col className="salary-offer__parent-col" span={12}>
          <Row className="salary-offer__content-row__col">
            <Col className="salary-offer__content-row__col" span={labelColSpan}>
              <div className="salary-offer__content-row__field salary-offer__content-row__field--total"></div>
            </Col>

            <Col
              className="salary-offer__content-row__col"
              span={currencyCode ? 14 : currencyColSpan}
            >
              <div className="salary-offer__content-row__field salary-offer__content-row__field--total"></div>
            </Col>
          </Row>
        </Col>

        <Col className="salary-offer__parent-col" span={12}>
          <Row className="salary-offer__content-row__col">
            <Col className="salary-offer__content-row__col" span={labelColSpan}>
              <div className="salary-offer__content-row__field salary-offer__content-row__field--total">
                <label>Difference</label>
              </div>
            </Col>

            <Col
              className="salary-offer__content-row__col"
              span={currencyCode ? 14 : currencyColSpan}
            >
              <div className="salary-offer__content-row__field salary-offer__content-row__field--total">
                <div
                  className={`w-full px-6   text-white shadow-md my-2 font-semibold text-center text-sm outline-none border border-themeInputBorder bg-themeBlue  py-2 rounded-md`}
                >
                  {/* {offer_fixed_cash?.proposed_total &&
                    offer_fixed_cash?.current_total
                    ? `${round(
                      (offer_fixed_cash?.proposed_total /
                        offer_fixed_cash?.current_total -
                        1) *
                      100
                    )}%` || ""
                    : "0%"} */}
                  {`${difference?.toFixed(2)}%`}
                </div>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default FixedCash;
